/**
 * SatelliteCardAtom - Satellite card with circular progress indicator
 *
 * Displays a satellite sphere as a card with:
 * - Circular progress ring showing gravity percentage
 * - Status-based color theming
 * - Avatar, initials, or percentage in center
 * - Title and subtitle text
 * - Hover effects and interactions
 */

import React, { useMemo, useState, useCallback } from 'react';
import type { Sphere, SatelliteSphere } from 'types/global';
import { getStatusColor } from '../../../utils/statusColors';
import type { ExtendedStatusValue } from '../../../utils/statusColors';
import { getOrbitalImage, ORBITAL_RING_DIMENSIONS } from '../../../utils/orbitalImages';
import { getGlyphIconDataUri } from '../../../utils/avatarIcons';
import './SatelliteCardAtom.scss';

export interface SatelliteCardAtomProps {
  /** Satellite sphere data (minimal) */
  satellite: SatelliteSphere;
  /** Full sphere object for avatar and subtitle */
  sphere?: Sphere;
  /** Card size in pixels (default: 90) */
  size?: number;
  /** Click handler */
  onClick?: () => void;
  /** Double-click handler */
  onDoubleClick?: () => void;
  /** Additional CSS class */
  className?: string;
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Get subtitle from sphere properties
 */
function getSubtitle(sphere?: Sphere): string {
  if (!sphere) return '';
  // Priority: type > description (truncated) > empty
  // Filter out "satellite" since it's redundant for satellite cards
  const type = sphere.properties?.type?.toLowerCase() === 'satellite'
    ? ''
    : sphere.properties?.type;

  return (
    type ||
    sphere.properties?.description?.substring(0, 30) ||
    ''
  );
}

const SatelliteCardAtom: React.FC<SatelliteCardAtomProps> = ({
  satellite,
  sphere,
  size = 90,
  onClick,
  onDoubleClick,
  className = '',
}) => {
  const [isFlipping, setIsFlipping] = useState(false);

  const handleDoubleClick = useCallback(() => {
    if (!onDoubleClick || isFlipping) return;
    setIsFlipping(true);
  }, [onDoubleClick, isFlipping]);

  const handleAnimationEnd = useCallback(() => {
    setIsFlipping(false);
    onDoubleClick?.();
  }, [onDoubleClick]);
  // Get status color and chrome image
  const statusColor = getStatusColor(satellite.status as ExtendedStatusValue);
  const orbitalChrome = getOrbitalImage();

  // Dimension calculations
  const nodeRadius = size / 2;
  const center = nodeRadius;

  // Status ring dimensions (matches orbital nodes)
  const statusRingRadius = nodeRadius * ORBITAL_RING_DIMENSIONS.ringCenterRadius; // 0.77
  const statusStrokeWidth = size * ORBITAL_RING_DIMENSIONS.strokeWidth; // 0.12

  // Progress ring dimensions (inside status ring)
  const progressStrokeWidth = 5;
  const progressRadius = nodeRadius * 0.65; // 65% radius
  const circumference = 2 * Math.PI * progressRadius;
  const progressPercentage = Math.max(0, Math.min(100, satellite.gravity));
  const progressOffset = circumference - (progressPercentage / 100) * circumference;

  // Inner content area (matches orbital nodes)
  const innerRadius = nodeRadius * 0.52; // 52% radius

  // Unique gradient ID for multiple instances
  const gradientId = `sat-status-gradient-${satellite.id}`;

  // Determine center content — prioritize avatar icon over percentage
  const avatarIconUri = getGlyphIconDataUri(satellite.glyph, satellite.glyphColor);
  const initials = getInitials(satellite.name);

  // Get subtitle
  const subtitle = useMemo(() => getSubtitle(sphere), [sphere]);

  return (
    <button
      type="button"
      className={`satellite-card-atom${isFlipping ? ' satellite-card-atom--flipping' : ''} ${className}`}
      onClick={onClick}
      onDoubleClick={handleDoubleClick}
      onAnimationEnd={handleAnimationEnd}
      title={satellite.name}
      style={{
        '--card-size': `${size}px`,
        '--status-color': statusColor,
      } as React.CSSProperties}
    >
      {/* Layered SVG with status ring, progress rings, chrome, and inner bg */}
      <svg
        className="satellite-card-atom__svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        <defs>
          {/* Status gradient (diagonal with opacity variation for shimmer) */}
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={statusColor} stopOpacity={0.9} />
            <stop offset="50%" stopColor={statusColor} stopOpacity={1} />
            <stop offset="100%" stopColor={statusColor} stopOpacity={0.9} />
          </linearGradient>
        </defs>

        {/* Layer 1: Status ring (bold status border) */}
        <circle
          className="satellite-card-atom__status-ring"
          cx={center}
          cy={center}
          r={statusRingRadius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={statusStrokeWidth}
        />

        {/* Layer 2: Progress ring background */}
        <circle
          className="satellite-card-atom__progress-bg"
          cx={center}
          cy={center}
          r={progressRadius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={progressStrokeWidth}
        />

        {/* Layer 3: Progress arc (gravity %) */}
        <circle
          className="satellite-card-atom__progress-bar"
          cx={center}
          cy={center}
          r={progressRadius}
          fill="none"
          stroke={statusColor}
          strokeWidth={progressStrokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={progressOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />

        {/* Layer 4: Chrome overlay (transparent PNG) */}
        <image
          className="satellite-card-atom__chrome-overlay"
          href={orbitalChrome}
          x={0}
          y={0}
          width={size}
          height={size}
          preserveAspectRatio="xMidYMid meet"
        />

        {/* Layer 5: Inner content background */}
        <circle
          className="satellite-card-atom__inner-bg"
          cx={center}
          cy={center}
          r={innerRadius}
          fill="#1a2744"
        />
      </svg>

      {/* Center Content — avatar icon from sphere schema type */}
      <div className="satellite-card-atom__center">
        {avatarIconUri ? (
          <img
            src={avatarIconUri}
            alt={satellite.name}
            className="satellite-card-atom__avatar"
          />
        ) : (
          <div className="satellite-card-atom__initials">{initials}</div>
        )}
      </div>

      {/* Label Section */}
      <div className="satellite-card-atom__label">
        <div className="satellite-card-atom__title">{satellite.name}</div>
        {subtitle && (
          <div className="satellite-card-atom__subtitle">{subtitle}</div>
        )}
      </div>
    </button>
  );
};

export default SatelliteCardAtom;